import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui';
import { AppAccessDialog } from '@/components/apps/AppAccessDialog';
import { AppSpecEditor } from '@/components/apps/AppSpecEditor';
import { type AppSummary, loadAppByKey } from '@/lib/app-access';
import { loadAppList } from '@/lib/app-records';
import { loadAppSpec, type StoredAppSpec } from '@/lib/app-spec';
import { supabase } from '@/lib/supabase';
import { defaultAppSpec } from '@shared/types/app-spec';
import type { ToolAppVisibility } from '@shared/types/tool-apps';

const ACCESS_LABELS: Record<ToolAppVisibility, string> = {
  private: 'Juste moi',
  restricted: 'Équipes choisies',
  org: 'Toute l’entreprise',
};

export interface ForgeApp {
  id: string;
  key: string;
  flowId: string;
}

/** The app of the tool this Forge conversation edits, once the tool is published. */
export const useForgeApp = (flowId: string | null, conversationId: string | null): ForgeApp | null => {
  const [app, setApp] = useState<ForgeApp | null>(null);
  useEffect(() => {
    let active = true;
    setApp(null);
    void (async () => {
      let id = flowId;
      if (!id && conversationId) {
        const { data } = await supabase.from('flow_conversations').select('flow_id').eq('id', conversationId).maybeSingle();
        id = (data as { flow_id: string | null } | null)?.flow_id ?? null;
      }
      if (!id) return;
      const { data } = await supabase.from('flows').select('id, app:tool_apps(id, key)').eq('id', id).maybeSingle();
      const row = data as { id: string; app: { id: string; key: string } | null } | null;
      if (active && row?.app) setApp({ id: row.app.id, key: row.app.key, flowId: row.id });
    })();
    return () => { active = false; };
  }, [flowId, conversationId]);
  return app;
};

/**
 * F6c, in the Forge only: the pages of the published app, changed by a
 * request in words and applied by its creator or a manager, and who can open
 * it. The app's own page and chat work on its content, never on its form.
 */
export const ForgeAppPages = ({ app }: { app: ForgeApp }) => {
  const [stored, setStored] = useState<StoredAppSpec | null>(null);
  const [summary, setSummary] = useState<AppSummary | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [hasReviewGrid, setHasReviewGrid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const refresh = useCallback(() => setReload((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    void Promise.all([loadAppSpec(app.id), loadAppList(app.id), loadAppByKey(app.key)])
      .then(([spec, list, found]) => { if (active) { setStored(spec); setHasReviewGrid(Boolean(list?.collection.reviewGrid)); setSummary(found); } })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : 'L’app n’a pas pu être chargée.'); });
    return () => { active = false; };
  }, [app.id, app.key, reload]);

  if (error) return <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p>;
  if (!stored) return <p role="status" className="text-sm text-domino-inkSoft">Chargement de l’app…</p>;
  const spec = stored.spec ?? defaultAppSpec({ hasReviewGrid });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-domino-inkSoft">
          Pages de l’app : {spec.pages.map((page) => page.label).join(' · ')}.{' '}
          <Link to={`/apps/${encodeURIComponent(app.key)}`} className="text-domino-aubergine underline">Ouvrir l’app</Link>
        </p>
        {summary && (
          <Button variant="secondary" size="sm" className="gap-2" onClick={() => setAccessOpen(true)} aria-label={`Accès : ${ACCESS_LABELS[summary.visibility]}`}>
            <Users className="h-4 w-4" />
            {ACCESS_LABELS[summary.visibility]}
          </Button>
        )}
      </div>
      <AppSpecEditor appId={app.id} appKey={app.key} spec={spec} version={stored.version}
        forgeHref={null} onApplied={refresh} />
      {accessOpen && summary && (
        <AppAccessDialog app={summary} onClose={() => setAccessOpen(false)}
          onSaved={(visibility) => { setSummary({ ...summary, visibility }); setAccessOpen(false); }} />
      )}
    </div>
  );
};
